import { useGame } from '../context/GameContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import { MedalIcon, ShareIcon } from '../components/icons';
import { AppScreen } from '../components/AppScreen';

export function EndPage() {
  const { endEvent, myPlayerId, leaveGame } = useGame();
  const { isDark, toggle } = useTheme();

  if (!endEvent) return null;

  const isWinner = endEvent.winnerId === myPlayerId;
  const winnerScore = endEvent.finalScores.find(f => f.playerId === endEvent.winnerId)?.score ?? 0;
  const myScore = endEvent.finalScores.find(f => f.playerId === myPlayerId)?.score;

  const handleShare = async () => {
    const text = isWinner
      ? `I just won a game of Plate Seeker with ${winnerScore} points!`
      : `Just played Plate Seeker — ${endEvent.winnerName} won with ${winnerScore} points. I scored ${myScore ?? 0}.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Plate Seeker', text });
      } catch {
        /* cancelled */
      }
    } else {
      await navigator.clipboard.writeText(text);
      alert('Results copied to clipboard!');
    }
  };

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-6">
        <span className="text-xs font-semibold uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
          Game Complete
        </span>
        <ThemeToggle isDark={isDark} onToggle={toggle} />
      </div>

      <main className="flex-1 px-6 pt-2 flex flex-col gap-5">
        <div className="flex flex-col items-center gap-2 pt-3">
          <MedalIcon className="w-[46px] h-[46px] text-atlas-brass dark:text-atlas-brass-dark" />
          <span className="font-serif text-2xl font-bold text-atlas-ink dark:text-atlas-ink-dark text-center">
            {isWinner ? 'You won the game' : `${endEvent.winnerName} won the game`}
          </span>
          <span className="font-mono text-4xl font-semibold text-atlas-brass dark:text-atlas-brass-dark">
            {winnerScore} pts
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Final Standings
          </span>
          {endEvent.finalScores
            .slice()
            .sort((a, b) => b.score - a.score)
            .map((entry, i) => {
              const isTheWinner = entry.playerId === endEvent.winnerId;
              return (
                <div
                  key={entry.playerId}
                  className="flex items-center gap-3 bg-atlas-surface dark:bg-atlas-surface-dark rounded-xl px-3.5 py-3"
                >
                  <div
                    className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-bold font-mono shrink-0 ${
                      isTheWinner
                        ? 'bg-atlas-brass dark:bg-atlas-brass-dark text-atlas-ink'
                        : 'border border-atlas-rule dark:border-atlas-rule-dark text-atlas-ink-soft dark:text-atlas-ink-soft-dark'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <span className="flex-1 text-[15px] font-semibold text-atlas-ink dark:text-atlas-ink-dark truncate">
                    {entry.playerName}
                    {entry.playerId === myPlayerId && (
                      <span className="ml-1 text-xs font-normal text-atlas-ink-soft dark:text-atlas-ink-soft-dark">(you)</span>
                    )}
                  </span>
                  <span className="font-mono text-base font-semibold text-atlas-ink dark:text-atlas-ink-dark">
                    {entry.score}
                  </span>
                </div>
              );
            })}
        </div>
      </main>

      <div className="sticky bottom-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 px-6 pt-2 pb-2 flex flex-col gap-2.5">
        <button
          onClick={leaveGame}
          className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all"
        >
          Play Again
        </button>
        <button
          onClick={handleShare}
          className="w-full h-11 rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[15px] flex items-center justify-center gap-2 hover:bg-atlas-surface dark:hover:bg-atlas-surface-dark transition-colors"
        >
          <ShareIcon className="w-4 h-4" />
          Share Results
        </button>
      </div>
    </AppScreen>
  );
}
